import { z } from "zod";

/** One text per language, keyed by language code ("en", "fr"...); a language may be missing or empty. */
export const localizedTextSchema = z.record(z.string().regex(/^[a-z]{2,3}$/), z.string().max(1000));

export type LocalizedText = z.infer<typeof localizedTextSchema>;

/** The text of one language: the asked one, else the main language, else the first filled one, else empty (a language the owner left blank never shows a hole). */
export function pickText(text: Readonly<Record<string, string>>, locale: string, main: string): string {
  const filled = (code: string) => (text[code]?.trim() ? text[code] : undefined);
  return filled(locale) ?? filled(main) ?? Object.keys(text).map(filled).find(Boolean) ?? "";
}

/** The languages that hold text but have no box (switched off): shown read-only so the text is not invisible; saving keeps it because the editor only ever spreads the saved object. */
export function switchedOffTexts(text: LocalizedText, shown: readonly string[]): string[] {
  return Object.keys(text).filter((code) => !shown.includes(code) && text[code]?.trim());
}

/** The texts with each cut to `max` characters (a translation can come out longer than the text it came from, and the menu refuses a text over the limit). */
export function clampTexts(texts: LocalizedText, max: number): LocalizedText {
  return Object.fromEntries(Object.entries(texts).map(([code, text]) => [code, text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text]));
}
